import { useMemo } from "react";
import dayjs from "dayjs";
import { usePeople } from "@/context/PeopleContext";
import type { PersonModelInterface, Payment } from "@/models/personModel";

export function getDebt(p: PersonModelInterface): number {
    const rd = Number(p?.creditDetail?.remainingDebt);
    if (Number.isFinite(rd)) return Math.max(0, rd);

    const total = Number(p?.creditDetail?.total ?? 0);
    const downPayment = Number(p?.creditDetail?.downPayment ?? 0);

    const paid = (p?.payments || [])
        .filter((pay) => pay?.type !== "discount")
        .reduce((sum, pay) => sum + Number(pay?.amount || 0), 0);

    const discountApplied = (p?.payments || [])
        .filter((pay) => pay?.type === "discount")
        .reduce((sum, pay) => sum + Number(pay?.amount || 0), 0);

    if (Number.isFinite(total) && total > 0) {
        const debt = total - (downPayment + paid + discountApplied);
        return Number.isFinite(debt) ? Math.max(0, debt) : 0;
    }

    return 0;
}

function statusKey(p: PersonModelInterface) {
    const s = (p?.status ?? "").toString().toLowerCase();
    if (s.includes("проср") || s === "overdue") return "overdue";
    if (s.includes("закры") || s === "closed" || s === "completed") return "closed";
    if (s.includes("доср") || s.includes("early")) return "closedEarly";
    if (s.includes("в процессе") || s === "pending") return "pending";
    if (s.includes("нов") || s === "new") return "new";
    if (getDebt(p) <= 0) return "closed";
    return "unknown";
}

function parseReleaseDay(raw: any) {
    if (!raw) return dayjs("");
    if (typeof raw === "string" && raw.includes(".")) {
        const [dd, mm, yyyy] = raw.split(".");
        if (dd && mm && yyyy) return dayjs(`${yyyy}-${mm}-${dd}`);
    }
    return dayjs(raw);
}

function getNextPaymentForToday(p: PersonModelInterface): number {
    const today = dayjs().startOf("day");
    const release = parseReleaseDay(p?.creditDetail?.releaseDay).startOf("day");
    if (!release.isValid()) return 0;

    if (getDebt(p) <= 0) return 0;
    if (today.date() !== release.date()) return 0;

    const mp = Number(p?.creditDetail?.monthlyPayment ?? 0);
    const monthlyPayment = Number.isFinite(mp) ? mp : 0;
    if (monthlyPayment <= 0) return 0;

    return Math.max(0, Math.min(monthlyPayment, getDebt(p)));
}

export function useClients() {
    const { people } = usePeople();

    const stats = useMemo(() => {
        const list = (people || []) as PersonModelInterface[];

        const totalDebt = list
            .filter((p) => statusKey(p) === "overdue")
            .reduce((sum, p) => sum + getDebt(p), 0);

        const activeCount = list.filter((p) => {
            const s = statusKey(p);
            return s !== "closed" && s !== "closedEarly" && getDebt(p) > 0;
        }).length;

        const overdueCount = list.filter(
            (p) => statusKey(p) === "overdue" && getDebt(p) > 0
        ).length;

        const todayPayments = list
            .filter((p) => {
                const s = statusKey(p);
                return s === "new" || s === "pending" || s === "overdue";
            })
            .reduce((sum, p) => sum + getNextPaymentForToday(p), 0);

        return {
            totalDebt,
            activeCount,
            overdueCount,
            todayPayments,
        };
    }, [people]);

    return { stats };
}